import apiClient from './ApiClient';

export class BookingRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/bookings';
	}

	/**
	 * Create a new booking (client or admin)
	 * @param {Object} payload
	 * @param {string} payload.clientName - Client name
	 * @param {string} payload.clientEmail - Client email
	 * @param {string} payload.clientPhone - Client phone
	 * @param {string} payload.timeSlotId - Time slot UUID
	 * @param {string} payload.dateSlotId - Date slot UUID
	 * @param {Array<string>} payload.serviceItemIds - Array of service item UUIDs
	 * @param {string} [payload.note] - Optional booking note
	 * @returns {Promise<{referenceCode: string}>}
	 */
	async create(payload) {
		return this.client.post(this.basePath, payload);
	}

	/**
	 * Get booking status by reference code - Public endpoint
	 * @param {string} referenceCode - Booking reference code
	 * @returns {Promise<Object>} Booking details
	 */
	async getByReferenceCode(referenceCode) {
		return this.client.get(`${this.basePath}/status/${referenceCode}`);
	}

	/**
	 * Get paginated bookings - Admin only
	 * @param {Object} params
	 * @param {number} params.page
	 * @param {number} params.pageSize
	 * @param {string} [params.status]
	 * @param {string} [params.startDate] - YYYY-MM-DD
	 * @param {string} [params.endDate]   - YYYY-MM-DD
	 * @returns {Promise<{items: Array, totalCount: number, totalPages: number, page: number, pageSize: number}>}
	 */
	async listPaged(params = {}) {
		return this.client.get(`${this.basePath}/admin/paged`, params);
	}

	/**
	 * Get all bookings with filtering - Admin only
	 * @param {Object} params - Optional query parameters
	 * @param {string} params.status - Filter by status (pending, confirmed, inprogress, completed, cancelled, noshow)
	 * @param {string} params.startDate - Format YYYY-MM-DD
	 * @param {string} params.endDate - Format YYYY-MM-DD
	 * @returns {Promise<Array>} Array of bookings
	 */
	async list(params = {}) {
		return this.client.get(`${this.basePath}/admin`, params);
	}

	/**
	 * Update booking status - Admin only
	 * @param {string} id - Booking UUID
	 * @param {string} status - New status (confirmed, in-progress, inprogress, completed, cancelled, no-show, noshow)
	 * @param {string} [cancellationReason] - Required if status is 'cancelled'
	 * @returns {Promise<{success: boolean}>}
	 */
	async updateStatus(id, status, cancellationReason = null) {
		const payload = { status };
		if (cancellationReason) {
			payload.cancellationReason = cancellationReason;
		}
		return this.client.patch(`${this.basePath}/${id}/status`, payload);
	}

			/**
		 * Bulk update booking statuses - Admin only
		 * @param {string[]} bookingIds - Array of Booking UUIDs
		 * @param {string} status - New status for all bookings (confirmed, in-progress, inprogress, completed, cancelled, no-show, noshow)
		 * @param {string} [cancellationReason] - Optional reason, only used when status is "cancelled"
		 * @returns {Promise<{succeeded: number, failed: number, failedIds: string[]}>}
		 */
		async bulkUpdateStatus(bookingIds, status, cancellationReason = null) {
			return this.client.patch(`${this.basePath}/bulk-status`, { bookingIds, status, cancellationReason });
		}

	/**
	 * Get booking by ID - Admin only
	 * @param {string} id - Booking UUID
	 * @returns {Promise<Object>} Booking details
	 */
	async getById(id) {
		return this.client.get(`${this.basePath}/${id}`);
	}

	// Legacy methods for compatibility
	async update(id, payload) {
		// If payload contains status, use updateStatus instead
		if (payload.status) {
			return this.updateStatus(id, payload.status, payload.cancellationReason);
		}
		// Otherwise, update is not supported directly in the API
		throw new Error('Direct booking updates not supported. Use updateStatus for status changes.');
	}

	/**
	 * Initiate Paystack payment for a booking
	 * @param {string} id - Booking UUID
	 * @param {string} callbackUrl - URL Paystack redirects to after payment
	 * @returns {Promise<{authorizationUrl: string, reference: string}>}
	 */
	async initiatePayment(id, callbackUrl) {
		return this.client.post(`${this.basePath}/${id}/initiate-payment`, { callbackUrl });
	}

	async finalizeItemPrice(bookingId, serviceItemId, finalUnitPrice) {
		return this.client.patch(
			`${this.basePath}/${encodeURIComponent(bookingId)}/items/${encodeURIComponent(serviceItemId)}/final-price`,
			{ finalUnitPrice }
		);
	}

	/**
	 * Verify payment after Paystack redirect (card / Apple Pay only)
	 * @param {string} reference - Paystack payment reference from query param
	 * @returns {Promise<{success: boolean}>}
	 */
	async verifyPayment(reference) {
		return this.client.post(`${this.basePath}/verify-payment?reference=${encodeURIComponent(reference)}`);
	}

	async cancel(id, cancellationReason) {
		return this.updateStatus(id, 'cancelled', cancellationReason);
	}

	async remove(id) {
		// The API doesn't support deleting bookings, only status changes
		return this.updateStatus(id, 'cancelled', 'Deleted by admin');
	}
}

export default new BookingRepository(apiClient);
